import { DynamicConfig, Experiment } from '@/contexts/dynamic-config';
import { getValueFromConfigs } from '@/contexts/dynamic-config-utils';
import { useMaybeUserContext, useUserContext } from '@/contexts/user-context';
import { useMemo } from 'react';

// Stable reference for the "no config" case so that the memo dependencies in
// useOrderSummaryConfig don't change every render when there's no user context
// / supplier config.
const EMPTY_CONFIG: DynamicConfig = {};

// Some app-wide gates should be based on only the supplier config, and not
// take chat overrides into account.
export const useSupplierDynamicConfigValue = <T extends keyof DynamicConfig>(
  key: T,
  defaultValue?: DynamicConfig[T],
): DynamicConfig[T] => {
  const userContextValue = useMaybeUserContext();

  let supplierDynamicConfig: DynamicConfig = {};

  // Account for guest mode
  if (
    userContextValue &&
    'selectedRestaurantRelationship' in userContextValue &&
    'supplierDynamicConfigMap' in userContextValue
  ) {
    const selectedSupplierUUID =
      userContextValue.selectedRestaurantRelationship.supplierUUID;

    supplierDynamicConfig =
      userContextValue.supplierDynamicConfigMap[selectedSupplierUUID];
  }

  return getValueFromConfigs(
    key,
    supplierDynamicConfig,
    undefined,
    defaultValue,
  );
};

export const useDynamicConfigValue = <T extends keyof DynamicConfig>(
  key: T,
  defaultValue?: DynamicConfig[T],
): DynamicConfig[T] => {
  const userContextValue = useMaybeUserContext();

  let chatDynamicConfig: DynamicConfig = {};
  let supplierDynamicConfig: DynamicConfig = {};

  if (
    userContextValue &&
    'selectedRestaurantRelationship' in userContextValue &&
    'supplierDynamicConfigMap' in userContextValue
  ) {
    const { selectedRestaurantRelationship, supplierDynamicConfigMap } =
      userContextValue;
    chatDynamicConfig = selectedRestaurantRelationship.chatDynamicConfig;
    supplierDynamicConfig =
      supplierDynamicConfigMap[selectedRestaurantRelationship.supplierUUID];
  }

  return getValueFromConfigs(
    key,
    supplierDynamicConfig,
    chatDynamicConfig,
    defaultValue,
  );
};

/**
 * Memoized accessor for object-typed config keys.
 * getValueFromConfigs deep-merges into a new object every call, so the
 * reference churns and breaks memoized callbacks/values downstream.
 * TODO: fix at the source and drop this wrapper.
 * https://usepepper.atlassian.net/browse/STOR-1795
 */
const useMemoizedDynamicConfigValue = <T extends keyof DynamicConfig>(
  key: T,
): DynamicConfig[T] => {
  const userContextValue = useMaybeUserContext();

  let chatDynamicConfig: DynamicConfig = EMPTY_CONFIG;
  let supplierDynamicConfig: DynamicConfig = EMPTY_CONFIG;

  if (
    userContextValue &&
    'selectedRestaurantRelationship' in userContextValue &&
    'supplierDynamicConfigMap' in userContextValue
  ) {
    const { selectedRestaurantRelationship, supplierDynamicConfigMap } =
      userContextValue;
    chatDynamicConfig = selectedRestaurantRelationship.chatDynamicConfig;
    supplierDynamicConfig =
      supplierDynamicConfigMap[selectedRestaurantRelationship.supplierUUID];
  }

  return useMemo(
    () => getValueFromConfigs(key, supplierDynamicConfig, chatDynamicConfig),
    [key, supplierDynamicConfig, chatDynamicConfig],
  );
};

export const useOrderSummaryConfig =
  (): DynamicConfig['order_summary_config'] =>
    useMemoizedDynamicConfigValue('order_summary_config');

export const useAvailabilityStatusConfig =
  (): DynamicConfig['availability_status_config'] =>
    useMemoizedDynamicConfigValue('availability_status_config');

/**
 * Whether catalog search is available when choosing substitutions.
 *
 * If `can_substitute_from_catalog` has not been explicitly set on either
 * dynamic config, fall back to `are_substitutions_enabled`.
 */
export const useSubstitutionCatalogSearchEnabled = (): boolean => {
  // No default value to allow for fallback
  const canSubstituteFromCatalog = useDynamicConfigValue(
    'can_substitute_from_catalog',
  );
  const areSubstitutionsEnabled = useDynamicConfigValue(
    'are_substitutions_enabled',
    false,
  );

  return canSubstituteFromCatalog ?? areSubstitutionsEnabled ?? false;
};

/**
 * Syntactic wrapper for getting a boolean flag from dynamic config,
 * checking both chat config and supplier config.
 */
export const useDynamicConfigFlag = <
  K extends keyof DynamicConfig,
  F extends keyof NonNullable<DynamicConfig[K]>,
>(
  key: K,
  flag: F,
): boolean => {
  const keyConfig = useDynamicConfigValue(key);
  if (typeof keyConfig !== 'object' || !keyConfig) {
    return false;
  }
  // NOTE: TS is not smart enough to infer that this type is valid,
  // but F gets correctly inferred externally by the caller.
  // @ts-ignore ts(2536)
  return Boolean(keyConfig[flag]);
};

/**
 * Same wrapper as above, but for supplier config only.
 */
export const useSupplierDynamicConfigFlag = <
  K extends keyof DynamicConfig,
  F extends keyof NonNullable<DynamicConfig[K]>,
>(
  key: K,
  flag: F,
  defaultValue = false,
): boolean => {
  const keyConfig = useSupplierDynamicConfigValue(key);
  if (typeof keyConfig !== 'object' || !keyConfig) {
    return defaultValue;
  }
  // @ts-ignore ts(2536)
  const value = keyConfig[flag] ?? defaultValue;
  return Boolean(value);
};

export const useGetDynamicConfigValueForChat = () => {
  const { restaurantRelationships, supplierDynamicConfigMap } =
    useUserContext();

  const getDynamicConfigValueForChat = <T extends keyof DynamicConfig>(
    chatUUID: string,
    key: T,
    defaultValue?: DynamicConfig[T],
  ): DynamicConfig[T] => {
    const relationship = restaurantRelationships.find(
      (relationship) => relationship.chatUUIDs[0] === chatUUID,
    );

    return relationship
      ? getValueFromConfigs(
          key,
          supplierDynamicConfigMap[relationship.supplierUUID],
          relationship.chatDynamicConfig,
          defaultValue,
        )
      : defaultValue;
  };

  return { getDynamicConfigValueForChat };
};

/**
 * Helper function to find an experiment by key in an array of experiments
 */
const findExperimentByKey = (
  experiments: Experiment[],
  key: string,
): Experiment | undefined => {
  return experiments.find((experiment) => experiment.key === key);
};

/**
 * Helper to get experiment status and metadata from an array of experiments.
 */
const getExperimentStatus = (
  experiments: Experiment[],
  experimentKey: string,
): { enabled: boolean; metadata?: Record<string, unknown> } => {
  const experiment = findExperimentByKey(experiments, experimentKey);
  if (experiment) {
    return {
      enabled: true,
      ...(experiment.metadata !== undefined && {
        metadata: experiment.metadata ?? {},
      }),
    };
  }
  return { enabled: false, metadata: undefined };
};

/**
 * Hook to get experiment status and metadata from both chat and supplier configs.
 * Prioritizes chat experiments over supplier experiments.
 * Returns an object with enabled status and optional metadata.
 */
export const useGetExperiment = (
  experimentKey: string,
): { enabled: boolean; metadata?: Record<string, unknown> } => {
  const userContextValue = useMaybeUserContext();

  let chatExperiments: Experiment[] = [];
  let supplierExperiments: Experiment[] = [];

  if (
    userContextValue &&
    'selectedRestaurantRelationship' in userContextValue &&
    'supplierDynamicConfigMap' in userContextValue
  ) {
    const { selectedRestaurantRelationship, supplierDynamicConfigMap } =
      userContextValue;
    chatExperiments =
      selectedRestaurantRelationship.chatDynamicConfig?.experiments || [];
    supplierExperiments =
      supplierDynamicConfigMap[selectedRestaurantRelationship.supplierUUID]
        ?.experiments || [];
  }

  // Check chat experiments first, then supplier
  const chatResult = getExperimentStatus(chatExperiments, experimentKey);
  if (chatResult.enabled) {
    return chatResult;
  }
  return getExperimentStatus(supplierExperiments, experimentKey);
};
